import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import CategoryDistributionChart from "@/components/extendedWarranty/reports/CategoryDistributionChart";
import PackageDistributionChart from "@/components/extendedWarranty/reports/PackageDistributionChart";
import PersonnelPerformanceTable from "@/components/extendedWarranty/reports/PersonnelPerformanceTable";
import SaleReportFilters from "@/components/extendedWarranty/reports/SaleReportFilters";
import SaleReportHeader from "@/components/extendedWarranty/reports/SaleReportHeader";
import SalesTrendChart from "@/components/extendedWarranty/reports/SalesTrendChart";
import SaleSummaryCards from "@/components/extendedWarranty/reports/SaleSummaryCards";
import StoreRankingTable from "@/components/extendedWarranty/reports/StoreRankingTable";

const SaleReports = () => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const {
    saleReports,
    saleReportFilters,
    loading,
    getSaleReports,
    updateSaleReportFilters,
    exportSaleReports,
    resetSaleReportFilters,
  } = useExtendedWarrantyStore();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        await getSaleReports();
        setError(null);
      } catch (err) {
        setError(t("extendedWarranties.errors.loadingReports"));
      }
    };

    fetchReports();
  }, []);

  const handleFilter = async (newFilters) => {
    try {
      await updateSaleReportFilters(newFilters);
      setError(null);
    } catch (err) {
      setError(t("extendedWarranties.errors.filteringError"));
    }
  };

  const handleResetFilters = async () => {
    try {
      await resetSaleReportFilters();
      setError(null);
    } catch (err) {
      setError(t("extendedWarranties.errors.resetFiltersError"));
    }
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      await exportSaleReports(format);
      setError(null);
    } catch (error) {
      setError(t("extendedWarranties.errors.exportError"));
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <SaleReportHeader onExport={handleExport} isExporting={isExporting} />

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-800">
          {error}
        </div>
      )}

      <SaleReportFilters
        filters={saleReportFilters}
        onFilterChange={handleFilter}
        onResetFilters={handleResetFilters}
      />

      <SaleSummaryCards
        totalSales={saleReports?.totalSales}
        totalRevenue={saleReports?.totalRevenue}
        averagePackagePrice={saleReports?.averagePackagePrice}
        activeStores={saleReports?.activeStores}
      />

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CategoryDistributionChart
          data={saleReports?.categoryDistribution || []}
        />
        <PackageDistributionChart
          data={saleReports?.packageDistribution || []}
        />
      </div>

      <SalesTrendChart data={saleReports?.salesTrend || []} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <StoreRankingTable
          data={saleReports?.storeRanking || []}
          loading={loading}
        />
        <PersonnelPerformanceTable
          data={saleReports?.personnelPerformance || []}
          loading={loading}
        />
      </div>
    </main>
  );
};

export default SaleReports;
